import React, { useState, useMemo } from 'react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { useTranslation } from 'react-i18next';
import Spring from '@/components/Spring';
import TabButton from '@/ui/TabButton';
import styles from './styles.module.scss';

interface ActivityData {
  date: string;
  predictions: number;
  points: number;
}

interface PredictionActivityProps {
  data?: ActivityData[];
  period?: '7d' | '30d' | 'season';
  onPeriodChange?: (period: '7d' | '30d' | 'season') => void;
}

const PERIOD_KEYS = ['7d', '30d', 'season'] as const;

// Mock data generator for demo
const generateMockData = (period: '7d' | '30d' | 'season'): ActivityData[] => {
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
  const data: ActivityData[] = [];
  const now = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      predictions: Math.floor(Math.random() * 5) + 1,
      points: Math.floor(Math.random() * 30) + 5
    });
  }
  return data;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  const { t } = useTranslation('tournament');
  if (active && payload && payload.length) {
    return (
      <div className={styles.tooltip}>
        <p className={styles.tooltipDate}>{label}</p>
        <p className={styles.tooltipValue}>
          <span className={styles.tooltipDot} style={{ backgroundColor: 'var(--highlight)' }} />
          {payload[0]?.value} {t('predictions').toLowerCase()}
        </p>
        {payload[1] && (
          <p className={styles.tooltipValue}>
            <span className={styles.tooltipDot} style={{ backgroundColor: 'var(--accent)' }} />
            {payload[1]?.value} {t('points').toLowerCase()}
          </p>
        )}
      </div>
    );
  }
  return null;
};

const PredictionActivity: React.FC<PredictionActivityProps> = ({
  data: externalData,
  period = '7d',
  onPeriodChange
}) => {
  const { t } = useTranslation('tournament');
  const [currentPeriod, setCurrentPeriod] = useState(period);

  const periodLabels: Record<string, string> = {
    '7d': t('period7d'),
    '30d': t('period30d'),
    'season': t('periodSeason'),
  };

  const chartData = useMemo(() => {
    return externalData || generateMockData(currentPeriod);
  }, [externalData, currentPeriod]);

  const handlePeriodChange = (newPeriod: '7d' | '30d' | 'season') => {
    setCurrentPeriod(newPeriod);
    onPeriodChange?.(newPeriod);
  };

  return (
    <Spring className="card">
      <div className={styles.header}>
        <h3 className={styles.title}>{t('predictionActivity')}</h3>
        <div className={styles.tabs}>
          {PERIOD_KEYS.map((key) => (
            <TabButton
              key={key}
              title={periodLabels[key]}
              active={currentPeriod === key}
              onClick={() => handlePeriodChange(key)}
            />
          ))}
        </div>
      </div>

      <div className={styles.chart}>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="predictionGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--highlight)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--highlight)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="pointsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={false}
              interval={currentPeriod === '7d' ? 0 : 'preserveStartEnd'}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="predictions"
              stroke="var(--highlight)"
              strokeWidth={2}
              fill="url(#predictionGradient)"
              dot={false}
              activeDot={{ r: 4, stroke: 'var(--widget)', strokeWidth: 2 }}
            />
            <Area
              type="monotone"
              dataKey="points"
              stroke="var(--accent)"
              strokeWidth={2}
              fill="url(#pointsGradient)"
              dot={false}
              activeDot={{ r: 4, stroke: 'var(--widget)', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: 'var(--highlight)' }} />
          <span>{t('predictions')}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: 'var(--accent)' }} />
          <span>{t('points')}</span>
        </div>
      </div>
    </Spring>
  );
};

export default PredictionActivity;
